import React from 'react';
import { format, parseISO } from 'date-fns';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { activationsService } from '../../api/services/activationsService';
import { Activation } from '../../types/domain';
import LoadingSpinner from '../common/LoadingSpinner';

type ActivationsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  licenseKey?: string;
};

const ActivationsModal: React.FC<ActivationsModalProps> = ({
  isOpen,
  onClose,
  licenseKey,
}) => {
  const queryClient = useQueryClient();
  
  // Query to fetch activations for the selected license
  const {
    data: activations = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['activations', licenseKey],
    queryFn: () => licenseKey ? activationsService.getActivationsByLicenseKey(licenseKey) : Promise.resolve([]),
    enabled: isOpen && !!licenseKey,
  });

  // Mutation to deactivate an activation
  const deactivateMutation = useMutation({
    mutationFn: ({ activationId, machineId }: { activationId: string; machineId: string }) => 
      activationsService.deactivateActivation(activationId, machineId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activations', licenseKey] });
      // Also invalidate licenses to update activation counts
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    }
  });
  
  // Mutation to reactivate an activation
  const reactivateMutation = useMutation({
    mutationFn: ({ activationId, machineId, licenseKey }: { activationId: string; machineId: string; licenseKey: string }) => 
      activationsService.reactivateActivation(activationId, machineId, licenseKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['activations', licenseKey] });
      // Also invalidate licenses to update activation counts
      queryClient.invalidateQueries({ queryKey: ['licenses'] });
    }
  });

  // Handle deactivation of a machine
  const handleDeactivate = (activation: Activation) => {
    deactivateMutation.mutate({
      activationId: activation.activationId,
      machineId: activation.machineId,
    });
  };
  
  // Handle reactivation of a machine
  const handleReactivate = (activation: Activation) => {
    if (!licenseKey) return;
    
    reactivateMutation.mutate({
      activationId: activation.activationId,
      machineId: activation.machineId,
      licenseKey: licenseKey
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-gray-900">License Activations</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 focus:outline-none"
            aria-label="Close"
          >
            <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {licenseKey && (
          <div className="mb-4 p-3 bg-gray-50 rounded-md">
            <p className="text-sm text-gray-500">License Key:</p>
            <p className="font-mono font-medium">{licenseKey}</p>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="p-4 border border-red-200 bg-red-50 text-red-700 rounded-md">
            <p>Error loading activations: {error instanceof Error ? error.message : 'Unknown error'}</p>
            <button
              onClick={() => refetch()}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        ) : activations.length === 0 ? (
          <p className="text-gray-500 text-center p-8">No activations found for this license.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Machine ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activation Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Checked
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {activations.map((activation) => (
                  <tr key={activation.activationId}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {activation.machineId}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        activation.status === 'active' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {activation.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {activation.activationType}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(activation.lastCheckedAt), 'PPp')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {format(parseISO(activation.createdAt), 'PPp')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {activation.status === 'active' ? (
                        <button
                          onClick={() => handleDeactivate(activation)}
                          disabled={deactivateMutation.isPending}
                          className="text-red-600 hover:text-red-900"
                        >
                          Deactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => handleReactivate(activation)}
                          disabled={reactivateMutation.isPending}
                          className="text-green-600 hover:text-green-900"
                        >
                          Reactivate
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivationsModal;